import { ArrowLeft, ArrowRight, ListUl } from "@boxicons/react";
import { router } from "@inertiajs/react";
import * as React from "react";
import typia from "typia";

import { getFolderArchiveDownloadUrl, getProductFileDownloadInfos, saveLastContentPage } from "$app/data/products";
import { AnalyticsData } from "$app/parsers/product";
import { RichContent, RichContentPage } from "$app/parsers/richContent";
import { assertDefined } from "$app/utils/assert";
import { CurrencyCode, getIsSingleUnitCurrency } from "$app/utils/currency";
import FileUtils from "$app/utils/file";
import {
  generatePageIcon,
  PAGE_ICON_COMPONENTS,
  PAGE_ICON_LABELS,
  type PageIconType,
} from "$app/utils/rich_content_page";
import { startTrackingForSeller, trackProductEvent } from "$app/utils/user_analytics";

import { Button } from "$app/components/Button";
import { DiscordButton } from "$app/components/DiscordButton";
import { DownloadAllButton } from "$app/components/Download/DownloadAllButton";
import { FileList as DownloadFileList, FileItem, FolderItem } from "$app/components/Download/FileList";
import { OpenInAppButton } from "$app/components/Download/OpenInAppButton";
import { PageList, PageListItem } from "$app/components/Download/PageListLayout";
import { DownloadPagePostList, Post } from "$app/components/Download/PostList";
import {
  FileDownloadInfo,
  FilesAndFoldersDownloadInfoProvider,
  RichContentView,
} from "$app/components/Download/RichContent";
import { TranscodingNoticeModal } from "$app/components/Download/TranscodingNoticeModal";
import { Popover, PopoverAnchor, PopoverClose, PopoverContent, PopoverTrigger } from "$app/components/Popover";
import { FileEmbed } from "$app/components/ProductEdit/ContentTab/FileEmbed";
import { showAlert } from "$app/components/server-components/Alert";
import { LicenseKey } from "$app/components/TiptapExtensions/LicenseKey";
import { PostsProvider } from "$app/components/TiptapExtensions/Posts";
import { Menu, MenuItemRadio } from "$app/components/ui/Menu";
import { useAddThirdPartyAnalytics } from "$app/components/useAddThirdPartyAnalytics";
import { useIsAboveBreakpoint } from "$app/components/useIsAboveBreakpoint";
import { useOriginalLocation } from "$app/components/useOriginalLocation";
import { useReactNativeMessage } from "$app/components/useReactNativeMessage";
import { useRunOnce } from "$app/components/useRunOnce";
import { WithTooltip } from "$app/components/WithTooltip";

import { Layout, LayoutProps, PurchaseCustomField } from "./Layout";

export type { PurchaseCustomField };

const PageIcon = ({ iconKey }: { iconKey: PageIconType }) => {
  const Component = PAGE_ICON_COMPONENTS[iconKey];
  return <Component className="size-5" aria-label={PAGE_ICON_LABELS[iconKey]} />;
};

const ContentFilesContext = React.createContext<FileItem[] | null>(null);
export const ContentFilesProvider = ContentFilesContext.Provider;
export const useContentFiles = () =>
  assertDefined(React.useContext(ContentFilesContext), "ContentFilesProvider is missing");

const IsMobileAppViewContext = React.createContext<boolean | null>(null);
export const IsMobileAppViewProvider = IsMobileAppViewContext.Provider;
export const useIsMobileAppView = () =>
  assertDefined(React.useContext(IsMobileAppViewContext), "IsMobileAppViewProvider is missing");

const PurchaseInfoContext = React.createContext<{
  purchaseId: string | null;
  redirectId: string;
  token: string;
} | null>(null);
export const PurchaseInfoProvider = PurchaseInfoContext.Provider;
export const usePurchaseInfo = () =>
  assertDefined(React.useContext(PurchaseInfoContext), "PurchaseInfoProvider is missing");

const MediaUrlsContext = React.createContext<
  [Record<string, string[]>, React.Dispatch<React.SetStateAction<Record<string, string[]>>>] | null
>(null);
export const MediaUrlsProvider = MediaUrlsContext.Provider;
export const useMediaUrls = () => assertDefined(React.useContext(MediaUrlsContext), "MediaUrlsProvider is missing");

const PurchaseCustomFieldsContext = React.createContext<PurchaseCustomField[]>([]);
export const PurchaseCustomFieldsProvider = PurchaseCustomFieldsContext.Provider;
export const usePurchaseCustomFields = () =>
  assertDefined(React.useContext(PurchaseCustomFieldsContext), "PurchaseCustomFieldsProvider is missing");

export type License = {
  license_key: string;
  is_multiseat_license: boolean;
  seats: number;
};

export type ContentProps = {
  rich_content_pages: RichContentPage[] | null;
  last_content_page_id: string | null;
  license: License | null;
  content_items: (FileItem | FolderItem)[];
  posts: Post[];
  video_transcoding_info: { transcode_on_first_sale: boolean } | null;
  custom_receipt: string | null;
  discord: { connected: boolean } | null;
  ios_app_url: string;
  android_app_url: string;
  download_all_button: { files: { url: string; filename: string | null }[] } | null;
  community_chat_url: string | null;
};

export type SellerAnalyticsProps = {
  seller_id: string;
  analytics: AnalyticsData;
  purchase_event: {
    permalink: string;
    purchase_external_id: string;
    product_name: string;
    value: number;
    currency: string;
    quantity: number;
    tax: string;
  };
};

export const WithContent = ({
  content,
  product_has_third_party_analytics,
  seller_analytics,
  audio_durations,
  latest_media_locations,
  ...props
}: LayoutProps & {
  content: ContentProps;
  product_has_third_party_analytics: boolean | null;
  seller_analytics: SellerAnalyticsProps | null;
  audio_durations?: Record<string, FileItem["duration"]> | undefined;
  latest_media_locations?: Record<string, FileItem["latest_media_location"]> | undefined;
}) => {
  const url = new URL(useOriginalLocation());
  const addThirdPartyAnalytics = useAddThirdPartyAnalytics();
  const contentFiles = React.useMemo(() => {
    const files = content.content_items.filter((item): item is FileItem => item.type === "file");
    return files.map((file) => {
      let updated = file;
      if (audio_durations) {
        const duration = audio_durations[file.id];
        if (duration !== null && duration !== undefined) {
          updated = { ...updated, duration, content_length: duration };
        }
      }
      if (latest_media_locations) {
        updated = { ...updated, latest_media_location: latest_media_locations[file.id] ?? null };
      }
      return updated;
    });
  }, [content.content_items, audio_durations, latest_media_locations]);

  const mediaUrlsValue = React.useState<Record<string, string[]>>({});

  useRunOnce(() => {
    if (url.searchParams.get("receipt") === "true" && props.purchase?.email) {
      showAlert(`Your purchase was successful! We sent a receipt to ${props.purchase.email}.`, "success");
      url.searchParams.delete("receipt");
      router.replace({ url: url.toString(), preserveState: true, preserveScroll: true });

      if (product_has_third_party_analytics && props.purchase.product_permalink)
        addThirdPartyAnalytics({
          permalink: props.purchase.product_permalink,
          location: "receipt",
          purchaseId: props.purchase.id,
        });

      if (seller_analytics) {
        const { seller_id, analytics, purchase_event } = seller_analytics;
        startTrackingForSeller(seller_id, analytics);
        trackProductEvent(seller_id, {
          action: "purchased",
          seller_id,
          permalink: purchase_event.permalink,
          purchase_external_id: purchase_event.purchase_external_id,
          product_name: purchase_event.product_name,
          value: purchase_event.value,
          valueIsSingleUnit: getIsSingleUnitCurrency(typia.assert<CurrencyCode>(purchase_event.currency)),
          currency: purchase_event.currency.toUpperCase(),
          quantity: purchase_event.quantity,
          tax: purchase_event.tax,
        });
      }
    }
  });
  const isDesktop = useIsAboveBreakpoint("lg");
  const pages = content.rich_content_pages ?? [];
  const getInitialPageIndex = () => {
    if (!content.last_content_page_id) return 0;
    const index = pages.findIndex((page) => page.page_id === content.last_content_page_id);
    return index >= 0 ? index : 0;
  };
  const [activePageIndex, setActivePageIndex] = React.useState(getInitialPageIndex());
  const activePage = pages[activePageIndex];

  const handlePageChange = React.useCallback(
    (newIndex: number) => {
      setActivePageIndex(newIndex);
      const newPage = pages[newIndex];
      if (newPage && props.purchase) {
        void saveLastContentPage(props.token, newPage.page_id);
      }
    },
    [pages, props.token, props.purchase],
  );
  const showPageList = pages.length > 1 || (pages.length === 1 && (pages[0]?.title ?? "").trim() !== "");

  const pageIcons = React.useMemo(
    () =>
      pages.map(({ description }) =>
        generatePageIcon({
          hasLicense: description ? nodeHasLicense(description) : false,
          fileIds: description ? findFileEmbeds(description) : [],
          allFiles: contentFiles,
        }),
      ),
    [pages, contentFiles],
  );

  React.useEffect(() => {
    if (!window.ReactNativeWebView || !showPageList) return;
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "tocData",
        payload: {
          pages: pages.map((page, index) => ({ page_id: page.page_id, title: page.title, icon: pageIcons[index] })),
          activePageIndex,
        },
      }),
    );
  }, [showPageList, activePageIndex, pages, pageIcons]);

  useReactNativeMessage((data) => {
    if (data.type === "mobileAppPageChange") {
      handlePageChange(data.payload.pageIndex);
    }
  });

  const hasPreviousPage = activePageIndex > 0;
  const hasNextPage = activePageIndex < pages.length - 1;
  const downloadableFiles: FileDownloadInfo[] = [];
  for (const f of contentFiles) {
    if (f.download_url && !f.external_link_url)
      downloadableFiles.push({ id: f.id, url: f.download_url, size: f.file_size || 0 });
  }
  const downloadInfo = {
    downloadableFiles,
    pdfStampingEnabled: contentFiles.some((f) => f.pdf_stamp_enabled),
    isMobileAppWebView: props.is_mobile_app_web_view,
    getFolderArchive: (folderId: string) =>
      getFolderArchiveDownloadUrl(Routes.url_redirect_download_archive_path(props.token, { folder_id: folderId })),
    getDownloadUrlsForFiles: async (ids: string[]) =>
      getProductFileDownloadInfos(
        Routes.url_redirect_download_product_files_path(props.token, { product_file_ids: ids }),
      ),
    hasStreamable: (ids: string[]) =>
      contentFiles.some((f) => ids.includes(f.id) && FileUtils.isFileExtensionStreamable(f.extension)),
  };
  const postsContext = {
    posts: content.rich_content_pages
      ? content.posts.map((post) => ({
          id: post.id,
          name: post.name,
          date: { type: "date" as const, value: post.action_at },
          url: post.view_url,
        }))
      : [],
    total: content.rich_content_pages ? content.posts.length : 0,
  };

  const purchaseInfo = { purchaseId: props.purchase?.id ?? null, redirectId: props.redirect_id, token: props.token };

  return (
    <Layout
      {...props}
      headerActions={
        <>
          {props.purchase && content.discord ? (
            <DiscordButton purchaseId={props.purchase.id} connected={content.discord.connected} />
          ) : null}
          {content.community_chat_url ? (
            <Button asChild color="accent">
              <a href={content.community_chat_url}>Community</a>
            </Button>
          ) : null}
          <OpenInAppButton iosAppUrl={content.ios_app_url} androidAppUrl={content.android_app_url} />
          {content.download_all_button ? (
            <DownloadAllButton
              zip_path={Routes.url_redirect_download_archive_path(props.token)}
              files={content.download_all_button.files}
            />
          ) : null}
        </>
      }
      pageList={
        showPageList && isDesktop ? (
          <PageList aria-label="Table of Contents">
            {pages.map((page, index) => (
              <PageListItem
                key={page.page_id}
                isSelected={index === activePageIndex}
                onClick={() => handlePageChange(index)}
                role="tab"
              >
                <PageIcon iconKey={pageIcons[index] ?? "text-only"} />
                <span className="flex-1">{page.title ?? "Untitled"}</span>
              </PageListItem>
            ))}
          </PageList>
        ) : null
      }
    >
      <PurchaseInfoProvider value={purchaseInfo}>
        <MediaUrlsProvider value={mediaUrlsValue}>
          <IsMobileAppViewProvider value={props.is_mobile_app_web_view}>
            {content.rich_content_pages !== null ? (
              activePage ? (
                <ContentFilesProvider value={contentFiles}>
                  <FilesAndFoldersDownloadInfoProvider value={downloadInfo}>
                    <PostsProvider value={postsContext}>
                      <PurchaseCustomFieldsProvider value={props.purchase?.purchase_custom_fields ?? []}>
                        <RichContentView
                          key={activePage.page_id}
                          richContent={activePage.description}
                          saleInfo={
                            props.purchase
                              ? {
                                  sale_id: props.purchase.id,
                                  product_id: props.purchase.product_id,
                                  product_permalink: props.purchase.product_permalink,
                                }
                              : null
                          }
                          license={content.license}
                        />
                      </PurchaseCustomFieldsProvider>
                    </PostsProvider>
                  </FilesAndFoldersDownloadInfoProvider>
                </ContentFilesProvider>
              ) : null
            ) : content.content_items.length > 0 ? (
              <DownloadFileList content_items={content.content_items} />
            ) : null}
          </IsMobileAppViewProvider>
        </MediaUrlsProvider>
      </PurchaseInfoProvider>

      {showPageList &&
      (!props.is_mobile_app_web_view || (typeof window !== "undefined" && !window.ReactNativeWebView)) ? (
        <div role="navigation" className="mt-auto flex gap-4 border-t border-border pt-4 lg:justify-end lg:pb-4">
          {isDesktop ? null : (
            <Popover>
              <PopoverAnchor>
                <PopoverTrigger aria-label="Table of Contents" asChild>
                  <Button size="icon">
                    <ListUl className="size-5" />
                  </Button>
                </PopoverTrigger>
              </PopoverAnchor>
              <PopoverContent
                sideOffset={4}
                className="max-h-[var(--radix-popover-content-available-height,80vh)] overflow-auto border-0 p-0 shadow-none"
              >
                <Menu>
                  {pages.map((page, index) => (
                    <PopoverClose key={page.page_id} asChild>
                      <MenuItemRadio
                        checked={index === activePageIndex}
                        onClick={() => {
                          handlePageChange(index);
                        }}
                      >
                        <PageIcon iconKey={pageIcons[index] ?? "text-only"} />
                        {page.title ?? "Untitled"}
                      </MenuItemRadio>
                    </PopoverClose>
                  ))}
                </Menu>
              </PopoverContent>
            </Popover>
          )}
          <WithTooltip position="top" tip={hasPreviousPage ? null : "No more pages"}>
            <Button
              disabled={!hasPreviousPage}
              onClick={() => handlePageChange(activePageIndex - 1)}
              className="flex-1 lg:flex-none"
            >
              <ArrowLeft className="size-5" />
              Previous
            </Button>
          </WithTooltip>
          <WithTooltip position="top" tip={hasNextPage ? null : "No more pages"}>
            <Button
              disabled={!hasNextPage}
              onClick={() => handlePageChange(activePageIndex + 1)}
              className="flex-1 lg:flex-none"
            >
              Next
              <ArrowRight className="size-5" />
            </Button>
          </WithTooltip>
        </div>
      ) : null}

      {content.video_transcoding_info ? (
        <TranscodingNoticeModal transcodeOnFirstSale={content.video_transcoding_info.transcode_on_first_sale} />
      ) : null}

      {content.rich_content_pages === null && content.posts.length > 0 ? (
        <div className="flex flex-col gap-4">
          <DownloadPagePostList posts={content.posts} />
        </div>
      ) : null}
    </Layout>
  );
};

const findFileEmbeds = (node: RichContent): string[] =>
  node.content?.flatMap((child) => {
    if (child.type === FileEmbed.name && child.attrs?.id) return typia.assert<string>(child.attrs.id);
    return findFileEmbeds(child);
  }) ?? [];

const COMMON_CONTAINER_NODE_TYPES = ["doc", "orderedList", "bulletList", "listItem", "blockquote"];
const nodeHasLicense = (node: RichContent) =>
  node.type === LicenseKey.name ||
  ((COMMON_CONTAINER_NODE_TYPES.includes(node.type ?? "") && node.content?.some(nodeHasLicense)) ?? false);
